import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import * as actionCreators from "../../store/actions/index";
import { Drawer, Dropdown, Button, Menu } from 'antd';
import EditEmployee from "../../components/EditEmployee/EditEmployee";
import NewEmployee from "../../components/NewEmployee/NewEmployee";
import classes from './Employees.module.scss';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faIdBadge, faTools } from '@fortawesome/free-solid-svg-icons';
import DataTable from '../../components/DataTable/DataTable';
import StringFormatter from '../../shared/stringFormatter';
import ColumnSearchFilter from '../../shared/tableColumnSearchFilter';
import { DeleteOutlined, DownOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router-dom';
import { isListNotEmpty, isNotNullOrUndefined, isStringEmpty, isStringNotEmpty } from '../../shared/objectUtils';
import { CanDo } from '../../shared/entitlements/entitlements';
import { selectListIsLoading, selectListPagination, selectListRecords } from '../../store/utility';
import RemoveEmployee from '../../components/RemoveEmployee/RemoveEmployee';

const stringFormatter = new StringFormatter();
const columnSearchFilter = new ColumnSearchFilter();

const Employees = ({ accountId, ...props }) => {
    //#region constants

    const fullWidth = global.window.innerWidth;
    const singularEntityName = "Employee";
    const pluralEntityName = "Employees";

    //#endregion
    //#region useDispatch and useSelectors

    const dispatch = useDispatch();
    const entityId = useSelector(state => state.auth.entityId);
    const entityType = useSelector(state => state.auth.entityType);
    const records = useSelector(state => selectListRecords(state.accountUsers.lists, `${isStringNotEmpty(accountId) ? accountId : ((isStringNotEmpty(entityId) && entityType !== 'STAFF') ? entityId : 'ALL')}_EMPLOYEES`));
    const isLoading = useSelector(state => selectListIsLoading(state.accountUsers.lists, `${isStringNotEmpty(accountId) ? accountId : ((isStringNotEmpty(entityId) && entityType !== 'STAFF') ? entityId : 'ALL')}_EMPLOYEES`));
    const pagination = useSelector(state => selectListPagination(state.accountUsers.lists, `${isStringNotEmpty(accountId) ? accountId : ((isStringNotEmpty(entityId) && entityType !== 'STAFF') ? entityId : 'ALL')}_EMPLOYEES`));

    //#endregion
    //#region useStates

    const [selectedRecord, setSelectedRecord] = useState({});
    const [showNewEntity, setShowNewEntity] = useState(false);
    const [showEditEntity, setShowEditEntity] = useState(false);
    const [showRemoveEntity, setShowRemoveEntity] = useState(false);
    const [searchText, setSearchText] = useState({ email: '', irisId: '', firstName: '', lastName: '' });

    //#endregion
    //#region toggles

    const toggleNewEntity = () => {
        setShowNewEntity(!showNewEntity);
    };

    const toggleEditEntity = () => {
        setShowEditEntity(!showEditEntity);
    };

    const toggleRemoveEntity = () => {
        setShowRemoveEntity(!showRemoveEntity);
    };

    //#endregion
    //#region goTos

    const goToEmployeeDashboard = (id) => {
        props.history.push({ pathname: `/employees/${id}` }, {
            previousPageTitle: "Employees",
            previousPageLocation: props.location,
            previousBreadcrumbs: props.breadcrumbs,
        });
    };

    //#endregion
    //#region table methods

    const refreshTable = () => {
        let searchParams = {
            page: 1,
            size: 5,
            'isDriver:or': false,
            'accountRoles:or': 'ADMIN|OPERATIONS_ADMIN|SAFETY_ADMIN|FINANCIAL_ADMIN|DISPATCHER|LOAD_PLANNER|CLERK|TRANSPORTATION_MANAGER',
            isDeleted: false
        };

        if (isStringNotEmpty(accountId)) {
            searchParams.accountId = accountId;
        } else if (isStringNotEmpty(entityId) && entityType !== 'STAFF') {
            searchParams.accountId = entityId;
        }

        dispatch(actionCreators.fetchAccountUserList(`${isStringNotEmpty(accountId) ? accountId : ((isStringNotEmpty(entityId) && entityType !== 'STAFF') ? entityId : 'ALL')}_EMPLOYEES`, searchParams));
    };

    const handleTableChange = (pagination, filters, sorter) => {
        const pager = { ...pagination };
        pager.current = pagination.current;
        dispatch(actionCreators.fetchAccountUserListSuccess(`${isStringNotEmpty(accountId) ? accountId : ((isStringNotEmpty(entityId) && entityType !== 'STAFF') ? entityId : 'ALL')}_EMPLOYEES`, {params:{ pagination: pager }}));

        let searchParams = {
            page: pagination.current,
            size: pagination.pageSize,
            'isDriver:or': false,
            'accountRoles:or': 'ADMIN|OPERATIONS_ADMIN|SAFETY_ADMIN|FINANCIAL_ADMIN|DISPATCHER|LOAD_PLANNER|CLERK|TRANSPORTATION_MANAGER',
            isDeleted: false
        };

        if (isStringNotEmpty(sorter.order)) {
            const sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
            searchParams.order = sortOrder;
        }
        if (isStringNotEmpty(sorter.field)) {
            searchParams.sort = sorter.field;
        }

        if (isStringNotEmpty(accountId)) {
            searchParams.accountId = accountId;
        } else if (isStringNotEmpty(entityId) && entityType !== 'STAFF') {
            searchParams.accountId = entityId;
        }

        if (isNotNullOrUndefined(filters)) {
            if (isListNotEmpty(filters.irisId)) {
                searchParams['irisId:contains'] = filters.irisId[0];
            }
            if (isListNotEmpty(filters.email)) {
                searchParams['email:contains'] = filters.email[0];
            }
            if (isListNotEmpty(filters.firstName)) {
                searchParams['firstName:contains'] = filters.firstName[0];
            }
            if (isListNotEmpty(filters.lastName)) {
                searchParams['lastName:contains'] = filters.lastName[0];
            }
        }

        dispatch(actionCreators.fetchAccountUserList(`${isStringNotEmpty(accountId) ? accountId : ((isStringNotEmpty(entityId) && entityType !== 'STAFF') ? entityId : 'ALL')}_EMPLOYEES`, searchParams));
    };

    //#endregion
    //#region table displays

    const menu = (record) => {
        return (
            <Menu>
                <Menu.Item key="editEntity" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); toggleEditEntity(); }}>
                    <FontAwesomeIcon className="anticon" icon={faIdBadge} style={{ marginRight: 8 }} />
                    <span>Edit Employee Details</span>
                </Menu.Item>
                <Menu.Item key="goToDashboard" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); goToEmployeeDashboard(record.id); }}>
                    <span>Go to Employee Dashboard</span>
                </Menu.Item>
                {CanDo({entityAction: 'DELETE', entityModel: 'ACCOUNT_USER', entityObject: record, accountId: isStringNotEmpty(accountId) ? accountId : ((isStringNotEmpty(entityId) && entityType !== 'STAFF') ? entityId : null)}) ? (
                    <Menu.Item key="deleteEntity" onClick={(e) => { e.domEvent.stopPropagation(); setSelectedRecord(record); toggleRemoveEntity(); }}>
                        <DeleteOutlined style={{ marginRight: 8 }} />
                        <span>Delete {singularEntityName}</span>
                    </Menu.Item>
                ) : null}
            </Menu>
        );
    };

    const columns = [
        {
            title: <FontAwesomeIcon icon={faTools} size="lg" />,
            key: 'actions',
            className: classes.columnNoBreak,
            width: 50,
            render: (text, record) => {
                return (
                    <Dropdown overlay={menu(record)} trigger={['click']}>
                        <Button>Actions <DownOutlined /></Button>
                    </Dropdown>
                );
            },
            align: 'center',
        },
        {
            title: 'Employee ID',
            dataIndex: 'irisId',
            key: 'irisId',
            ...columnSearchFilter.getColumnSearchProps('irisId', 'Employee Id', searchText, setSearchText)
        },
        {
            title: 'First Name',
            dataIndex: 'firstName',
            key: 'firstName',
            ...columnSearchFilter.getColumnSearchProps('firstName', 'First Name', searchText, setSearchText)
        },
        {
            title: 'Last Name',
            dataIndex: 'lastName',
            key: 'lastName',
            ...columnSearchFilter.getColumnSearchProps('lastName', 'Last Name', searchText, setSearchText)
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            ...columnSearchFilter.getColumnSearchProps('email', 'Email', searchText, setSearchText)
        },
        {
            title: 'Phone',
            dataIndex: ['userProfile', 'phone'],
            key: 'userProfile.phone',
            render: (text, record) => { return stringFormatter.toFormattedString("PhoneNumber", text, record.userProfile.phoneExt);  },
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
        },
        {
            title: 'Role(s)',
            dataIndex: 'accountRoles',
            key: 'accountRoles',
            render: (text, record) => { return isListNotEmpty(text) ? text.map((accountRole) => stringFormatter.toFormattedString("AccountRole", accountRole)).join(', ') : '' },
        },
        {
            title: 'Work Phone',
            dataIndex: 'workPhone',
            key: 'workPhone',
            render: (text, record) => { return stringFormatter.toFormattedString("PhoneNumber", text, record.workPhoneExt);  },
        },
        {
            title: 'Is Verified',
            dataIndex: 'isVerified',
            key: 'isVerified',
            render: (text, record) => { return stringFormatter.toFormattedString("Boolean", text, null); },
        }
    ];

    if (CanDo({ staffOnly: true }) && isStringEmpty(accountId)) {
        columns.push({
            title: 'Account Name',
            dataIndex: ['account', 'name'],
            key: 'account.name',
        });
    }

    //#endregion
    //#region displays

    const newEntityComponents = (
        <Drawer
            title={"New " + singularEntityName}
            onClose={toggleNewEntity}
            visible={showNewEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <NewEmployee
                cancel={toggleNewEntity}
                accountId={isStringNotEmpty(accountId) ? accountId : (isStringNotEmpty(entityId) && entityType !== 'STAFF' ? entityId : null)}
            />
        </Drawer>
    );

    const editEntityComponents = (
        <Drawer
            title={"Edit " + singularEntityName}
            onClose={toggleEditEntity}
            visible={showEditEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <EditEmployee
                cancel={toggleEditEntity}
                employee={selectedRecord}
            />
        </Drawer>
    );

    const removeEntityComponents = (
        <Drawer
            title={"Delete " + singularEntityName}
            width={fullWidth > 720 ? fullWidth / 2 : 360}
            onClose={toggleRemoveEntity}
            visible={showRemoveEntity === true}
            bodyStyle={{ paddingBottom: 80 }}
            style={{ zIndex: 1000 }}
            destroyOnClose={true}
            closable={false}
            maskClosable={false}
        >
            <RemoveEmployee
                cancel={toggleRemoveEntity}
                accountUser={selectedRecord}
                singularEntityName={singularEntityName}
            />
        </Drawer>
    );

    //#endregion
    //#region useEffects

    useEffect(() => {
        refreshTable();
    }, [accountId, entityId]);

    //#endregion

    return (
        <DataTable
            dataSource={records}
            columns={columns}
            pagination={pagination}
            onChange={handleTableChange}
            loading={isLoading === true}
            rowClassName={classes.dataTableRow}
            singularEntityName={singularEntityName}
            pluralEntityName={pluralEntityName}
            newEntityAction={toggleNewEntity}
            rowKey={record => record.id}
            style={{ backgroundColor: '#ffffff' }}
        >
            {newEntityComponents}
            {editEntityComponents}
            {removeEntityComponents}
        </DataTable>
    );
};

export default withRouter(Employees);